import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import {
  getAssistant,
  getMyRating,
  type ListAssistantsParams,
  listAssistants,
  listCategories,
  listMyAssistants,
  listPinnedAssistants,
} from "../api/getAssistants";
import { listModels } from "../api/listModels";

/** The public catalogue, filtered/sorted/paged by `params`. */
export function useAssistants(params: ListAssistantsParams = {}) {
  return useQuery({
    queryKey: queryKeys.assistants.list({
      search: params.search,
      sort: params.sort,
      category: params.category,
      limit: params.limit,
    }),
    queryFn: () => listAssistants(params),
    staleTime: 60_000,
  });
}

/** The model catalogue, for the editor's default-model picker (shared cache). */
export function useModelCatalog() {
  return useQuery({
    queryKey: queryKeys.chat.models(),
    queryFn: listModels,
    staleTime: 5 * 60_000,
    // Same key + same cached shape as the chat models query; we only need the list.
    select: (data) => data.items ?? [],
  });
}

/** The category taxonomy (long stale time — it rarely changes). */
export function useAssistantCategories() {
  return useQuery({
    queryKey: queryKeys.assistants.categories(),
    queryFn: listCategories,
    staleTime: 10 * 60_000,
  });
}

/** One assistant's detail page payload. */
export function useAssistant(id: string) {
  return useQuery({
    queryKey: queryKeys.assistants.detail(id),
    queryFn: () => getAssistant(id),
    staleTime: 2 * 60_000,
  });
}

/** The signed-in user's own assistants. */
export function useMyAssistants() {
  return useQuery({
    queryKey: queryKeys.assistants.mine(),
    queryFn: listMyAssistants,
    staleTime: 2 * 60_000,
  });
}

/** The user's pinned assistants (also powers the sidebar). */
export function usePinnedAssistants() {
  return useQuery({
    queryKey: queryKeys.assistants.pinned(),
    queryFn: listPinnedAssistants,
    staleTime: 2 * 60_000,
  });
}

/** The user's current rating for one assistant (0 = unrated). */
export function useMyRating(id: string) {
  return useQuery({
    queryKey: queryKeys.assistants.myRating(id),
    queryFn: () => getMyRating(id),
    staleTime: 2 * 60_000,
  });
}

/**
 * Derived ownership/pin lookup sets, shared by the cards and detail view so a
 * single `/me` + `/pinned` fetch drives every "is this mine / pinned?" decision.
 */
export function useAssistantFlags() {
  const mine = useMyAssistants();
  const pinned = usePinnedAssistants();
  const ownedIds = useMemo(
    () => new Set((mine.data ?? []).map((a) => a.id)),
    [mine.data],
  );
  const pinnedIds = useMemo(
    () => new Set((pinned.data ?? []).map((a) => a.id)),
    [pinned.data],
  );
  return { ownedIds, pinnedIds };
}
